import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Flask, Package, ClipboardText, Table as TableIcon, Gear, CircleNotch, Warning, Truck, Clock, Lock } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "@/components/ui/sonner";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { DailyRegister } from "@/components/consumption/DailyRegister";
import { ArrivalsPanel } from "@/components/consumption/ArrivalsPanel";
import { ClosingCount } from "@/components/consumption/ClosingCount";
import { WeekReviewGrid } from "@/components/consumption/WeekReviewGrid";
import { SapPostingPanel } from "@/components/consumption/SapPostingPanel";
import { DepartmentSelfSetup } from "@/components/consumption/DepartmentSelfSetup";
import { StoRequestPanel } from "@/components/consumption/StoRequestPanel";
import { api, registerError, dayLabel } from "@/components/consumption/registerApi";

const DEPT_KEY = "cr:department";
const PROC_KEY = "cr:process";

// The phone keeps its last department+process, because a plating operator
// opens the same screen every single day.
const remember = (key, value) => {
  try { value ? localStorage.setItem(key, value) : localStorage.removeItem(key); } catch (e) { /* private mode */ }
};
const recall = (key) => {
  try { return localStorage.getItem(key) || ""; } catch (e) { return ""; }
};

const WeekStrip = ({ week }) => {
  const tone = week.next_week_blocked
    ? "bg-[#FEF3F2] border-[#FDA29B] text-[#B42318]"
    : week.count_due
      ? "bg-[#FFFAEB] border-[#FEDF89] text-[#B54708]"
      : "bg-white border-[#D0D5DD] text-[#475467]";
  return (
    <div className={`border rounded-sm px-3 py-2 text-sm ${tone}`} data-testid="week-strip">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="font-heading font-bold">Week {week.week_no}</span>
        {week.status === "open" ? (
          <span data-testid="week-strip-day">Day {week.day_index} of 7 · {week.start_date} to {week.end_date}</span>
        ) : (
          <span data-testid="week-strip-pending">Not started - the week begins on your first consumption entry</span>
        )}
      </div>
      {week.next_week_blocked && (
        <p className="mt-1 flex items-start gap-1.5 font-medium" data-testid="week-strip-blocked">
          <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
          Day 7 has passed. Today's figures are still accepted, but the next week only opens once the closing count is in.
        </p>
      )}
      {!week.next_week_blocked && week.count_due && (
        <p className="mt-1 flex items-start gap-1.5 font-medium" data-testid="week-strip-count-due">
          <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
          Closing count is due - count what is physically at the line and close the week.
        </p>
      )}
    </div>
  );
};

// Oct 26 2026, user: "you need to allowed user for updating past day today
// 10:00 AM add flag message on the Consumption Register page". Always on, with
// the time left, so the floor knows exactly how long yesterday stays open.
const EntryWindowFlag = ({ window: w }) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  if (!w) return null;
  const left = Math.max(0, Math.round((new Date(w.closes_at) - new Date()) / 60000));
  const open = w.yesterday_open && left > 0;
  const hours = Math.floor(left / 60);
  const countdown = hours > 0 ? `${hours} h ${left % 60} min left` : `${left} min left`;
  return (
    <div
      className={`border rounded-sm px-3 py-2 text-sm flex items-start gap-1.5 ${
        open ? "bg-[#FFFAEB] border-[#FEDF89] text-[#B54708]" : "bg-[#F9FAFB] border-[#D0D5DD] text-[#475467]"
      }`}
      data-testid="entry-window-flag"
    >
      {open ? <Clock size={14} weight="bold" className="shrink-0 mt-0.5" /> : <Lock size={14} weight="bold" className="shrink-0 mt-0.5" />}
      {open ? (
        <span>
          <span className="font-bold">Yesterday ({dayLabel(w.yesterday)}) closes at {w.cutoff_label}</span>
          {" "}· {countdown}. After that only today&apos;s figures can be recorded.
        </span>
      ) : (
        <span>
          <span className="font-bold">Yesterday ({dayLabel(w.yesterday)}) is closed</span>
          {" "}- it shut at {w.cutoff_label} this morning. Record today&apos;s figures; a day already with SAP
          changes only through the head&apos;s correction.
        </span>
      )}
    </div>
  );
};

// Back-dating is allowed only inside the open week, so the day picker is
// the week's own 7 days with anything in the future switched off.
const DayPicker = ({ week, today, value, onChange, window: w }) => {
  if (week.status !== "open" || !(week.days || []).length) return null;
  return (
    <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="day-picker">
      {week.days.map((d, idx) => {
        const future = d > today;
        // Closed by the 10:00 AM rule: anything before yesterday, and
        // yesterday itself once the morning window has passed.
        const closed = !future && d < today && !(w && d === w.yesterday && w.yesterday_open);
        const active = d === value;
        return (
          <button
            key={d}
            type="button"
            disabled={future}
            onClick={() => onChange(d)}
            className={`shrink-0 min-w-[64px] px-2.5 py-1.5 rounded-sm border text-center transition-colors duration-150 ${
              active
                ? "bg-[#0E7C86] border-[#0E7C86] text-white"
                : future
                  ? "bg-[#F2F4F7] border-[#EAECF0] text-[#98A2B3]"
                  : closed
                    ? "bg-[#F9FAFB] border-[#EAECF0] text-[#98A2B3] hover:bg-[#F2F4F7]"
                    : "bg-white border-[#D0D5DD] text-[#344054] hover:bg-[#F9FAFB]"
            }`}
            data-testid={`day-chip-${d}`}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Day {idx + 1}</div>
            <div className="text-[13px] font-bold">{dayLabel(d)}</div>
          </button>
        );
      })}
    </div>
  );
};

export default function ConsumptionRegisterPage() {
  const [masters, setMasters] = useState(null);
  const [deptId, setDeptId] = useState(recall(DEPT_KEY));
  const [processId, setProcessId] = useState(recall(PROC_KEY));
  const [register, setRegister] = useState(null);
  const [date, setDate] = useState(null);
  const [tab, setTab] = useState("today");
  const [focusProductId, setFocusProductId] = useState(null);
  const [reviewKey, setReviewKey] = useState(0);
  const [loadingMasters, setLoadingMasters] = useState(true);
  const [loadingRegister, setLoadingRegister] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await api.masters();
        setMasters(data);
      } catch (err) {
        toast.error(registerError(err).message);
      } finally {
        setLoadingMasters(false);
      }
    })();
  }, []);

  const reloadMasters = useCallback(async () => {
    try {
      setMasters(await api.masters());
    } catch (err) {
      toast.error(registerError(err).message);
    }
  }, []);

  const departments = useMemo(() => masters?.departments || [], [masters]);
  const processes = useMemo(
    () => (masters?.processes || []).filter((p) => p.department_id === deptId),
    [masters, deptId],
  );
  const departmentItems = useMemo(() => {
    const ids = new Set(processes.map((p) => p.id));
    return (masters?.items || []).filter((i) => ids.has(i.process_id));
  }, [masters, processes]);

  // A remembered department/process that the user can no longer see (access
  // changed, item archived) must never leave the screen stuck on nothing.
  useEffect(() => {
    if (!masters) return;
    const valid = departments.some((d) => d.id === deptId);
    if (!valid) {
      const next = departments[0]?.id || "";
      setDeptId(next);
      remember(DEPT_KEY, next);
    }
  }, [masters, departments, deptId]);

  useEffect(() => {
    if (!masters || !deptId) return;
    if (!processes.some((p) => p.id === processId)) {
      const next = processes[0]?.id || "";
      setProcessId(next);
      remember(PROC_KEY, next);
    }
  }, [masters, deptId, processes, processId]);

  const loadRegister = useCallback(async (forDate) => {
    if (!processId) { setRegister(null); return; }
    setLoadingRegister(true);
    try {
      const data = await api.register(processId, forDate || undefined);
      setRegister(data);
      setDate(data.date);
    } catch (err) {
      setRegister(null);
      toast.error(registerError(err).message);
    } finally {
      setLoadingRegister(false);
    }
  }, [processId]);

  useEffect(() => { loadRegister(null); }, [loadRegister]);

  const onSaved = (data) => {
    setRegister(data);
    setDate(data.date);
    setReviewKey((k) => k + 1);
  };

  const pickDate = (d) => {
    setDate(d);
    loadRegister(d);
  };

  const claimFor = (productId) => {
    setFocusProductId(productId);
    setTab("arrivals");
  };

  const onClosed = () => {
    setReviewKey((k) => k + 1);
    loadRegister(null);
    setTab("week");
  };

  const selectedDepartment = departments.find((d) => d.id === deptId);

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex flex-col" data-testid="consumption-register-page">
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Flask size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Consumption Register</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <div className="p-3 sm:p-5 w-full max-w-[1400px] mx-auto space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Flask size={20} weight="fill" className="text-[#0E7C86]" />
              <h1 className="font-heading text-lg font-bold text-[#101828]">Consumption Register</h1>
              {register?.department?.posting_enabled ? (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#ECFDF3] text-[#027A48] border border-[#A6F4C5] rounded-sm px-1.5 py-0.5" data-testid="dry-run-badge">
                  day posting on
                </span>
              ) : (
                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#EFF8FF] text-[#175CD3] border border-[#B2DDFF] rounded-sm px-1.5 py-0.5" data-testid="dry-run-badge">
                  day posting off · movements still post
                </span>
              )}
            </div>
            <p className="text-sm text-[#667085] mt-0.5 max-w-2xl">
              Chemicals and consumables held by a department: what was used each day, proved by a physical count every seventh day.
            </p>
          </div>
          {masters?.is_admin && (
            <Link
              to="/admin/consumption-register-setup"
              className="flex items-center gap-1.5 text-[13px] font-bold text-[#0E7C86] hover:text-[#0B6B74]"
              data-testid="setup-link"
            >
              <Gear size={15} weight="bold" /> Register Setup
            </Link>
          )}
        </div>

        {loadingMasters ? (
          <div className="py-16 text-center" data-testid="masters-loading">
            <CircleNotch size={24} weight="bold" className="animate-spin text-[#0E7C86] mx-auto" />
          </div>
        ) : departments.length === 0 ? (
          <div className="rounded-sm border border-[#D0D5DD] bg-white p-8 text-center" data-testid="no-departments">
            <p className="text-sm text-[#667085]">
              No department has been set up for you yet. An administrator creates departments, processes and the
              materials each one holds under Register Setup.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sticky top-0 bg-[#F9FAFB] py-1 z-[5]">
              <Select
                value={deptId}
                onValueChange={(v) => { setDeptId(v); remember(DEPT_KEY, v); }}
              >
                <SelectTrigger className="h-11" data-testid="department-select">
                  <SelectValue placeholder="Department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`department-option-${d.id}`}>
                      {d.name} · {d.site_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={processId}
                onValueChange={(v) => { setProcessId(v); remember(PROC_KEY, v); }}
                disabled={processes.length === 0}
              >
                <SelectTrigger className="h-11" data-testid="process-select">
                  <SelectValue placeholder={processes.length ? "Process" : "No process yet"} />
                </SelectTrigger>
                <SelectContent>
                  {processes.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`process-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {processes.length === 0 ? (
              <>
                {selectedDepartment?.can_manage ? (
                  <DepartmentSelfSetup
                    department={selectedDepartment}
                    processes={processes}
                    items={departmentItems}
                    onChanged={reloadMasters}
                  />
                ) : (
                  <div className="rounded-sm border border-[#D0D5DD] bg-white p-8 text-center" data-testid="no-processes">
                    <p className="text-sm text-[#667085]">
                      {selectedDepartment?.name} has no process yet (plating line, powder coating booth, phosphating tank...).
                      Your department head or an administrator adds them.
                    </p>
                  </div>
                )}
                {/* Staging material into HOLD has nothing to do with a process,
                    so it stays available before any process exists. */}
                <StoRequestPanel key={selectedDepartment?.id} department={selectedDepartment} />
              </>
            ) : (
              <>
                {register && <WeekStrip week={register.week} />}
                {register && <EntryWindowFlag window={register.entry_window} />}

                <Tabs value={tab} onValueChange={setTab} className="w-full">
                  <TabsList className="w-full h-auto flex-wrap justify-start gap-1" data-testid="register-tabs">
                    <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
                    <TabsTrigger value="arrivals" data-testid="tab-arrivals">
                      <Package size={14} weight="bold" className="mr-1" /> Arrivals
                    </TabsTrigger>
                    <TabsTrigger value="count" data-testid="tab-count">
                      <ClipboardText size={14} weight="bold" className="mr-1" /> Closing count
                    </TabsTrigger>
                    <TabsTrigger value="week" data-testid="tab-week">
                      <TableIcon size={14} weight="bold" className="mr-1" /> Week review
                    </TabsTrigger>
                    <TabsTrigger value="sto" data-testid="tab-sto">
                      <Truck size={14} weight="bold" className="mr-1" /> STO request
                    </TabsTrigger>
                    {selectedDepartment?.can_manage && (
                      <TabsTrigger value="setup" data-testid="tab-setup">
                        <Gear size={14} weight="bold" className="mr-1" /> My department
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="today" className="mt-3">
                    <div className="max-w-2xl space-y-3">
                      {register && (
                        <DayPicker week={register.week} today={register.today} value={date} onChange={pickDate} window={register.entry_window} />
                      )}
                      {register && date && date !== register.today && (
                        <p className="text-xs font-bold text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1.5" data-testid="back-dated-notice">
                          Recording for {dayLabel(date)}, not today.
                        </p>
                      )}
                      {loadingRegister && !register ? (
                        <div className="py-12 text-center" data-testid="register-loading">
                          <CircleNotch size={22} weight="bold" className="animate-spin text-[#0E7C86] mx-auto" />
                        </div>
                      ) : (
                        <DailyRegister
                          key={`${processId}:${register?.date}`}
                          register={register}
                          onSaved={onSaved}
                          onClaimHandover={claimFor}
                        />
                      )}
                      {register && !register.needs_opening_balance && register.items.length > 0 && (
                        <SapPostingPanel
                          key={`${register.department.id}:${register.date}:${register.items.reduce((sum, i) => sum + Number(i.declared || 0), 0)}`}
                          register={register}
                          onChanged={() => loadRegister(register.date)}
                        />
                      )}
                    </div>
                  </TabsContent>

                  <TabsContent value="arrivals" className="mt-3">
                    <div className="max-w-2xl">
                      <ArrivalsPanel
                        key={processId}
                        processId={processId}
                        focusProductId={focusProductId}
                        canManage={!!selectedDepartment?.can_manage}
                        onClaimed={onSaved}
                      />
                    </div>
                  </TabsContent>

                  <TabsContent value="count" className="mt-3">
                    <div className="max-w-2xl">
                      <ClosingCount key={`${processId}:${reviewKey}`} processId={processId} onClosed={onClosed} />
                    </div>
                  </TabsContent>

                  <TabsContent value="week" className="mt-3">
                    <WeekReviewGrid processId={processId} refreshKey={reviewKey} />
                  </TabsContent>

                  <TabsContent value="sto" className="mt-3">
                    <div className="max-w-2xl">
                      <StoRequestPanel key={selectedDepartment?.id} department={selectedDepartment} />
                    </div>
                  </TabsContent>

                  {selectedDepartment?.can_manage && (
                    <TabsContent value="setup" className="mt-3">
                      <DepartmentSelfSetup
                        department={selectedDepartment}
                        processes={processes}
                        items={departmentItems}
                        onChanged={reloadMasters}
                      />
                    </TabsContent>
                  )}
                </Tabs>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
